import './DatePicker.css'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

/**
 * Moderný výber dátumu a času (rovnaký súbor je v administrácii aj v e-shope).
 * Hodnoty: dátum 'RRRR-MM-DD', dátum s časom 'RRRR-MM-DDTHH:mm' (miestny čas).
 */

const WEEKDAYS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']
const MONTHS = ['január', 'február', 'marec', 'apríl', 'máj', 'jún', 'júl', 'august', 'september', 'október', 'november', 'december']

const pad = (n: number) => String(n).padStart(2, '0')
export const toIsoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const parseIsoDay = (value: string) => { const [y, m, d] = value.split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1) }
export const todayIso = () => toIsoDay(new Date())
export const addDaysIso = (value: string, days: number) => { const d = parseIsoDay(value); d.setDate(d.getDate() + days); return toIsoDay(d) }

/** „št 29. 10. 2026“ / „štvrtok 29. októbra“ */
export function formatDay(value: string, long = false): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return ''
  const d = parseIsoDay(value)
  return long
    ? d.toLocaleDateString('sk-SK', { weekday: 'long', day: 'numeric', month: 'long' })
    : d.toLocaleDateString('sk-SK', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' })
}

type DayRules = { min?: string; max?: string; allowed?: readonly string[]; isDisabled?: (day: string) => boolean }
const dayAllowed = (day: string, rules: DayRules) =>
  (!rules.min || day >= rules.min) && (!rules.max || day <= rules.max) && (!rules.allowed || rules.allowed.includes(day)) && !rules.isDisabled?.(day)

function usePopover() {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc, true)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc, true) }
  }, [open])
  return { open, setOpen, box }
}

/** Mesačný kalendár. */
export function Calendar({ value, onSelect, rules = {}, markToday = true }: { value: string; onSelect: (day: string) => void; rules?: DayRules; markToday?: boolean }) {
  const initial = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseIsoDay(value) : rules.allowed?.[0] ? parseIsoDay(rules.allowed[0]) : rules.min && rules.min > todayIso() ? parseIsoDay(rules.min) : new Date()
  const [month, setMonth] = useState(() => new Date(initial.getFullYear(), initial.getMonth(), 1))
  const today = todayIso()
  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1)
    const offset = (first.getDay() + 6) % 7
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    return [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => toIsoDay(new Date(month.getFullYear(), month.getMonth(), i + 1)))]
  }, [month])
  const shift = (n: number) => setMonth(m => new Date(m.getFullYear(), m.getMonth() + n, 1))
  const prevDisabled = rules.min ? toIsoDay(new Date(month.getFullYear(), month.getMonth(), 0)) < rules.min : false
  const nextDisabled = rules.max ? toIsoDay(new Date(month.getFullYear(), month.getMonth() + 1, 1)) > rules.max : false
  return <div className="dp-cal">
    <div className="dp-cal-head">
      <button type="button" onClick={() => shift(-1)} disabled={prevDisabled} aria-label="Predchádzajúci mesiac">‹</button>
      <strong>{MONTHS[month.getMonth()]} {month.getFullYear()}</strong>
      <button type="button" onClick={() => shift(1)} disabled={nextDisabled} aria-label="Nasledujúci mesiac">›</button>
    </div>
    <div className="dp-grid" role="grid">
      {WEEKDAYS.map(w => <span key={w} className="dp-wd">{w}</span>)}
      {cells.map((day, i) => day === null ? <span key={`e${i}`} /> : <button key={day} type="button" role="gridcell"
        className={`dp-day ${day === value ? 'is-selected' : ''} ${markToday && day === today ? 'is-today' : ''} ${rules.allowed?.includes(day) ? 'is-available' : ''}`}
        disabled={!dayAllowed(day, rules)} aria-pressed={day === value} aria-label={formatDay(day, true)} onClick={() => onSelect(day)}>
        {Number(day.slice(8))}
      </button>)}
    </div>
  </div>
}

/** Pole s dátumom – klik otvorí kalendár. */
export function DateField({ value, onChange, placeholder = 'Vyberte dátum', min, max, allowed, isDisabled, clearable = true, label, required, className = '' }: {
  value: string; onChange: (value: string) => void; placeholder?: string; min?: string; max?: string; allowed?: readonly string[]; isDisabled?: (day: string) => boolean
  clearable?: boolean; label?: ReactNode; required?: boolean; className?: string
}) {
  const { open, setOpen, box } = usePopover()
  return <div className={`dp-field ${className} ${open ? 'is-open' : ''}`} ref={box}>
    {label && <span className="dp-label">{label}</span>}
    <button type="button" className={`dp-trigger ${value ? 'has-value' : ''}`} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 2v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2H7zm-2 7h14v10H5V9z" /></svg>
      <span>{value ? formatDay(value) : placeholder}</span>
    </button>
    {clearable && value && <button type="button" className="dp-clear" onClick={() => onChange('')} aria-label="Vymazať dátum">×</button>}
    {required && <input className="dp-required" tabIndex={-1} required value={value} onChange={() => {}} aria-hidden="true" />}
    {open && <div className="dp-pop" role="dialog">
      <Calendar value={value} rules={{ min, max, allowed, isDisabled }} onSelect={day => { onChange(day); setOpen(false) }} />
      <div className="dp-foot">
        {dayAllowed(todayIso(), { min, max, allowed, isDisabled }) && <button type="button" onClick={() => { onChange(todayIso()); setOpen(false) }}>Dnes</button>}
        {dayAllowed(addDaysIso(todayIso(), 1), { min, max, allowed, isDisabled }) && <button type="button" onClick={() => { onChange(addDaysIso(todayIso(), 1)); setOpen(false) }}>Zajtra</button>}
        {clearable && value && <button type="button" onClick={() => { onChange(''); setOpen(false) }}>Vymazať</button>}
      </div>
    </div>}
  </div>
}

/** Časy po krokoch, napr. 8:00, 8:30 … */
export function timeSlots(from = '07:00', to = '19:00', stepMinutes = 30): string[] {
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m }
  const out: string[] = []
  for (let m = toMin(from); m <= toMin(to); m += stepMinutes) out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`)
  return out
}

/** Výber času ako tlačidlá. Časy pred `notBefore` (pri dnešku) sú vypnuté. */
export function TimeSlots({ value, onChange, slots, disabledBefore }: { value: string; onChange: (time: string) => void; slots: readonly string[]; disabledBefore?: string }) {
  return <div className="dp-times" role="radiogroup" aria-label="Čas">
    {slots.map(t => <button key={t} type="button" role="radio" aria-checked={value === t} className={value === t ? 'is-selected' : ''} disabled={Boolean(disabledBefore && t < disabledBefore)} onClick={() => onChange(value === t ? '' : t)}>{t}</button>)}
  </div>
}

/** Dátum s časom v jednom poli (napr. návrh termínu v administrácii). */
export function DateTimeField({ value, onChange, placeholder = 'Vyberte dátum a čas', minDate, slots = timeSlots(), minLeadMinutes = 60, label, allowDayOnly = false }: {
  value: string; onChange: (value: string) => void; placeholder?: string; minDate?: string; slots?: readonly string[]; minLeadMinutes?: number; label?: ReactNode
  /** Čas je nepovinný – stačí deň. */ allowDayOnly?: boolean
}) {
  const { open, setOpen, box } = usePopover()
  const [day, time] = value ? value.split('T') : ['', '']
  const earliest = new Date(Date.now() + minLeadMinutes * 60000)
  const notBefore = day === toIsoDay(earliest) ? `${pad(earliest.getHours())}:${pad(earliest.getMinutes())}` : day && day < toIsoDay(earliest) ? '99:99' : undefined
  const set = (d: string, t: string) => onChange(d && t ? `${d}T${t}` : d ? `${d}T` : '')
  const shown = day ? `${formatDay(day)}${time ? ` · ${time}` : ''}` : ''
  return <div className={`dp-field ${open ? 'is-open' : ''}`} ref={box}>
    {label && <span className="dp-label">{label}</span>}
    <button type="button" className={`dp-trigger ${value ? 'has-value' : ''}`} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 5v5.4l3.6 2.1-1 1.7L11 13.6V7h2z" /></svg>
      <span>{shown || placeholder}</span>
    </button>
    {value && <button type="button" className="dp-clear" onClick={() => onChange('')} aria-label="Vymazať">×</button>}
    {open && <div className="dp-pop is-wide" role="dialog">
      <div className="dp-split">
        <Calendar value={day} rules={{ min: minDate ?? toIsoDay(earliest) }} onSelect={d => set(d, time && !(d === toIsoDay(earliest) && time < `${pad(earliest.getHours())}:${pad(earliest.getMinutes())}`) ? time : '')} />
        <div className="dp-time-col">
          <span className="dp-label">{day ? `Čas – ${formatDay(day, true)}` : 'Najprv vyberte deň'}</span>
          {day && <TimeSlots value={time} slots={slots} disabledBefore={notBefore} onChange={t => set(day, t)} />}
        </div>
      </div>
      <div className="dp-foot"><button type="button" className="is-primary" disabled={!day || (!time && !allowDayOnly)} onClick={() => setOpen(false)}>Hotovo</button></div>
    </div>}
  </div>
}
